import React, { useState, useEffect } from "react";
import axiosInstance from "../../Axios/axiosInstance";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Divider,
  Box,
  Snackbar,
  Select,
  MenuItem,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

const EmployeeUpdate = () => {
  const { employeeId } = useParams();
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    dob: null,
    age: "",
    role: "",
    phoneno: "",
    joiningdate: null,
    address: "",
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [errors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDOBChange = (date) => {
    let formattedDate = null;

    if (date) {
      formattedDate = dayjs(date).isValid() ? dayjs(date).toISOString() : null;
    }

    setFormData({
      ...formData,
      dob: formattedDate,
    });
  };

  const handleJoiningDateChange = (date) => {
    let formattedJoiningDate = null;

    if (date) {
      formattedJoiningDate = dayjs(date).isValid()
        ? dayjs(date).toISOString()
        : null;
    }

    setFormData({
      ...formData,
      joiningdate: formattedJoiningDate,
    });
  };

  const handleSuccessClose = () => {
    setOpenSuccess(false);
  };

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/getEmployee/${employeeId}`
        );
        if (response.status === 200) {
          const employeeData = response.data.employee;
          // Assuming date fields are in ISO string format, adjust accordingly if not
          setFormData({
            ...employeeData,
            dob: employeeData.dob ? dayjs(employeeData.dob).toDate() : null,
            joiningdate: employeeData.joiningdate
              ? dayjs(employeeData.joiningdate).toDate()
              : null,
          });
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchEmployeeData();
  }, [employeeId]);

  const handleEditClick = () => {
    setIsEditMode(true); // Enable edit mode
    setIsSubmitting(false); // Reset submission status on edit click
  };
  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axiosInstance.put(
        `/api/updateEmployee/${employeeId}`,
        formData
      );
      if (response.status === 200) {
        setSuccessMessage("Employee updated successfully");
        setOpenSuccess(true);
        setIsEditMode(false);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error updating employee data:", error.response.data);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleUpdate}>
        <Grid container spacing={3}>
          {/* User Registration Heading */}
          <Grid container item xs={12}>
            <Grid item xs={8}>
              <Typography variant="h5" align="left">
                User Registration
              </Typography>
            </Grid>
            <Grid item xs={4} className="text-end">
              <EditIcon
                style={{
                  cursor: "pointer",
                }}
                onClick={handleEditClick}
              />
            </Grid>
          </Grid>

          {/* Divider line */}
          <Grid item xs={12}>
            <Box>
              <Divider
                variant="fullWidth"
                style={{ backgroundColor: "black" }}
              />
            </Box>
          </Grid>

          {/* User Details */}
          <Grid item xs={12} className="">
            <Typography variant="h6" align="left">
              User Details
            </Typography>
          </Grid>

          {/* -----------input fields---------------------------------------- */}
          <Grid item xs={12} className="text-center" lg={6} md={6} sm={12}>
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">First Name</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="firstname"
                  value={formData.firstname || ""}
                  onChange={handleInputChange}
                  size="small"
                  disabled={!isEditMode || isSubmitting}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className="text-center" lg={6} md={6} sm={12}>
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">Last Name</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="lastname"
                  value={formData.lastname || ""}
                  onChange={handleInputChange}
                  size="small"
                  disabled={!isEditMode || isSubmitting}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Date Of Birth */}

          <Grid item xs={12} lg={6} md={6} sm={12} className="">
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">Date Of Birth</Typography>
              </Grid>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.dob ? dayjs(formData.dob) : null}
                    onChange={handleDOBChange}
                    renderInput={(params) => <TextField {...params} />}
                    disabled={!isEditMode || isSubmitting}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className="text-center" lg={6} md={6} sm={12}>
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">Age</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="age"
                  value={formData.age || ""}
                  onChange={handleInputChange}
                  size="small"
                  disabled={!isEditMode || isSubmitting}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6} md={6} sm={12}>
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">Email Id</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  value={formData.email || ""}
                  onChange={handleInputChange}
                  error={errors.email} // Show error style if true
                  helperText={errors.email ? "Email ID is required" : ""}
                  disabled={!isEditMode || isSubmitting}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} lg={6} md={6} sm={12}>
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">Phone Number</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="phoneno"
                  value={formData.phoneno || ""}
                  size="small"
                  onChange={handleInputChange}
                  error={errors.phoneno} // Show error style if true
                  helperText={errors.phoneno ? "Phone Number is required" : ""}
                  disabled={!isEditMode || isSubmitting}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} lg={6} md={6} sm={12}>
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">Role</Typography>
              </Grid>
              <Grid item xs={6}>
                <Select
                  fullWidth
                  name="role"
                  value={formData.role || ""}
                  onChange={handleInputChange}
                  disabled={!isEditMode || isSubmitting}
                >
                  <MenuItem value="">Select Role</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                  {/* Add more MenuItem components for other roles */}
                </Select>
              </Grid>
            </Grid>
          </Grid>

          {/* Joining Date */}
          <Grid item xs={12} lg={6} md={6} sm={12} className="">
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">Joining Date</Typography>
              </Grid>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      formData.joiningdate ? dayjs(formData.joiningdate) : null
                    }
                    onChange={handleJoiningDateChange}
                    renderInput={(params) => <TextField {...params} />}
                    disabled={!isEditMode || isSubmitting}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} lg={6} md={6} sm={12} className="">
            <Grid container alignItems="center">
              <Grid item xs={6} style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">Address</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  name="address"
                  value={formData.address || ""}
                  onChange={handleInputChange}
                  disabled={!isEditMode || isSubmitting}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* ----------- Submit Date ---------------------------------------- */}

          <Grid item xs={12} className="text-center mt-5">
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isEditMode || isSubmitting}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
      {/* Success Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSuccess}
        autoHideDuration={3000}
        onClose={handleSuccessClose}
        message={successMessage}
      />
    </>
  );
};

export default EmployeeUpdate;
